import { ActionIcon, Badge, Group, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { IconDownload, IconFile } from '@tabler/icons-react';
import { useConversationFiles } from 'src/secret-menu/conversation-debugger/hooks/useConversationFiles';

function formatFileSize(bytes: number | null): string | null {
  if (bytes === null) {
    return null;
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ConversationFiles({
  conversationId,
  tenantName,
}: {
  conversationId: string;
  tenantName: string;
}) {
  const { files, isError } = useConversationFiles(conversationId, tenantName);

  if (isError) {
    return (
      <Text size="textSm" c="red">
        Failed to load the files attached to this conversation.
      </Text>
    );
  }

  if (!files || files.length === 0) {
    return null;
  }

  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap={6}>
        <Text size="textXs" c="dimmed">
          Attached files ({files.length})
        </Text>
        {files.map(file => (
          <Group key={file.id} gap="xs" wrap="nowrap">
            <IconFile size={14} style={{ flexShrink: 0, color: 'var(--mantine-color-gray-6)' }} />
            <Text size="textXs" truncate="end" style={{ minWidth: 0 }}>
              {file.filename}
            </Text>
            {file.fileType && (
              <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                {file.fileType}
              </Badge>
            )}
            {formatFileSize(file.fileSize) && (
              <Text size="textXs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {formatFileSize(file.fileSize)}
              </Text>
            )}
            {file.createdAt && (
              <Text size="textXs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {new Date(file.createdAt).toLocaleString()}
              </Text>
            )}
            <Tooltip label="Download" withArrow>
              <ActionIcon
                component="a"
                href={file.downloadUrl}
                download={file.filename}
                size="sm"
                variant="subtle"
                color="gray"
                ml="auto"
              >
                <IconDownload size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ))}
      </Stack>
    </Paper>
  );
}
